import React from 'react';
import { Badge, Box, Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import { Buildings, CaretRight, FileText } from 'phosphor-react';
import { INotasAcompanhamentoEmpresa } from '../../../../../../types/notasAcompanhamento';
import { formatCNPJ } from '../../../../../../utils/FormatFunctions';

interface NotasAcompEmpresaCardProps {
  empresa: INotasAcompanhamentoEmpresa;
  // eslint-disable-next-line no-unused-vars
  onClick: (empresa: INotasAcompanhamentoEmpresa) => void;
}

const formatNumber = (v?: number) => (v ?? 0).toLocaleString('pt-BR');

export function NotasAcompEmpresaCard({ empresa, onClick }: NotasAcompEmpresaCardProps) {
  const nome = empresa.razao_social || empresa.nome;
  const totalNotas = empresa.qtd_notas_total ?? 0;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      p={4}
      cursor="pointer"
      transition="all 0.2s"
      role="group"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={() => onClick(empresa)}
    >
      <Flex justify="space-between" align="center" mb={3} gap={2}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500">
          #{empresa.external_id}
        </Text>
        {empresa.uf && (
          <Badge colorScheme="blue" fontSize="2xs" px={2} borderRadius="full">
            {empresa.uf}
          </Badge>
        )}
      </Flex>

      <Flex align="center" gap={2} mb={1}>
        <Icon as={Buildings} color="#1976D2" boxSize={5} flexShrink={0} />
        <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={2}>
          {nome || 'Empresa não identificada'}
        </Text>
      </Flex>
      <Text fontSize="xs" color="gray.500" pl={7} mb={1}>
        {empresa.cnpj ? formatCNPJ(empresa.cnpj) : '—'}
      </Text>
      {(empresa.cidade || empresa.uf) && (
        <Text fontSize="xs" color="gray.500" pl={7} mb={3} noOfLines={1}>
          {[empresa.cidade, empresa.uf].filter(Boolean).join(' / ')}
        </Text>
      )}

      <Flex gap={2} mb={3} wrap="wrap">
        <Tooltip label="Entradas" hasArrow>
          <Badge colorScheme="green" variant="subtle" fontSize="2xs" px={2} py={0.5}>
            E: {formatNumber(empresa.qtd_notas_entrada)}
          </Badge>
        </Tooltip>
        <Tooltip label="Saídas" hasArrow>
          <Badge colorScheme="orange" variant="subtle" fontSize="2xs" px={2} py={0.5}>
            S: {formatNumber(empresa.qtd_notas_saida)}
          </Badge>
        </Tooltip>
        <Tooltip label="Serviços" hasArrow>
          <Badge colorScheme="purple" variant="subtle" fontSize="2xs" px={2} py={0.5}>
            Sv: {formatNumber(empresa.qtd_notas_servico)}
          </Badge>
        </Tooltip>
      </Flex>

      {empresa.regime_tributario_atualizado && (
        <Tooltip label={empresa.regime_tributario_atualizado} hasArrow>
          <Text fontSize="2xs" color="gray.500" mb={3} noOfLines={1}>
            {empresa.regime_tributario_atualizado}
          </Text>
        </Tooltip>
      )}

      <Box borderTop="1px" borderColor="gray.100" pt={3}>
        <Flex justify="space-between" align="center" gap={2}>
          {empresa.grupo_de_empresas ? (
            <Text fontSize="2xs" color="gray.500" noOfLines={1}>
              {empresa.grupo_de_empresas}
            </Text>
          ) : (
            <Box />
          )}
          <Flex align="center" gap={1}>
            <Icon as={FileText} color="gray.400" boxSize={4} />
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {formatNumber(totalNotas)} {totalNotas === 1 ? 'nota' : 'notas'}
            </Text>
          </Flex>
        </Flex>
      </Box>

      <Flex
        justify="flex-end"
        align="center"
        gap={1}
        mt={2}
        color="gray.400"
        _groupHover={{ color: 'blue.500' }}
        transition="color 0.2s"
      >
        <Text fontSize="xs" fontWeight="medium">
          Ver livros
        </Text>
        <Icon as={CaretRight} boxSize={3} weight="bold" />
      </Flex>
    </Box>
  );
}
